import React from 'react';
import { Toolbar, Button, Typography } from '@mui/material';
import first from 'lodash/first';
import max from 'lodash/max';
import min from 'lodash/min';
import moment from 'moment';
import { startCase } from '@pv/common/utils';
import { useNavigate } from 'react-router-dom';
import { ProviderVenuesQuery } from '@pv/common/graphql/operations';
import { useVenues } from '../../../providers/venues';
import { useUserAuth } from '../../../providers/auth';
import { PVMediumPurple } from '@pv/common/colors';
import { useUserAgent } from '@pv/core/src/hooks/useUserAgent';
import { BANNER_HEIGHT, useNavLayout } from '../NavLayoutContext';

type ProviderVenue = ProviderVenuesQuery['venues'][number];

type TrialKind = 'initial' | 'plan-tier';

type TrialInfo = {
  daysLeft: number;
  tierName: string;
  willAutoUpgrade: boolean;
  kind: TrialKind;
};

const BOOK_A_DEMO_URL =
  'https://meetings.hubspot.com/luke486/demo-request-from-in-the-product';

const daysUntil = (endDate: string | null | undefined): number | null => {
  if (!endDate) return null;
  const diff = moment(endDate).startOf('day').diff(moment().startOf('day'), 'days');
  return max([0, diff]) ?? 0;
};

const trialInfoForVenue = (venue: ProviderVenue): TrialInfo | null => {
  if (venue.activePlanTierTrial) {
    const daysLeft = daysUntil(venue.activePlanTierTrial.endDate);
    if (daysLeft === null) return null;
    return {
      daysLeft,
      tierName: startCase(venue.activePlanTierTrial.trialTier),
      willAutoUpgrade: venue.activePlanTierTrial.willAutoUpgrade,
      kind: 'plan-tier',
    };
  }
  if (venue.subscription?.tier === 'trial') {
    const daysLeft = daysUntil(venue.subscription.endDate);
    if (daysLeft === null) return null;
    return {
      daysLeft,
      tierName: 'Free',
      willAutoUpgrade: false,
      kind: 'initial',
    };
  }
  return null;
};

const soonestTrial = (venues: ProviderVenue[]): TrialInfo | null => {
  const trials = venues
    .map(trialInfoForVenue)
    .filter((t): t is TrialInfo => t !== null);
  if (trials.length === 0) return null;
  const minDays = min(trials.map((t) => t.daysLeft)) ?? 0;
  return trials.find((t) => t.daysLeft === minDays) ?? null;
};

const FreeTrialAppBar = () => {
  const { venues } = useVenues();
  const { user } = useUserAuth();
  const { isWeb } = useUserAgent();
  const navigate = useNavigate();
  const { trialBannerVisible } = useNavLayout();

  const canManageBilling = Boolean(
    user?.organization?.canManageBilling?.value
  );

  const venueList = venues ?? [];
  const firstVenueStarter =
    first(venueList)?.subscription?.tier === 'starter';
  const trial = firstVenueStarter ? null : soonestTrial(venueList);
  const onStarter = firstVenueStarter;

  const isNonPayingState =
    (trial && trial.kind === 'initial') || onStarter;

  let text: React.ReactNode = null;
  let buttonText = '';
  let buttonDestination = '';
  if (trial) {
    if (trial.daysLeft === 0) {
      text = (
        <span>
          Your <strong>{trial.tierName}</strong> trial ends today
        </span>
      );
    } else {
      const dayWord = trial.daysLeft === 1 ? 'Day' : 'Days';
      text = (
        <span>
          <strong>{trial.daysLeft}</strong> {dayWord} Left on Your{' '}
          {trial.tierName} Trial
        </span>
      );
    }
    if (trial.kind === 'initial') {
      buttonText = 'Upgrade';
      buttonDestination = '/pricing';
    } else if (!trial.willAutoUpgrade && canManageBilling) {
      buttonText = 'Upgrade';
      buttonDestination = '/settings/billing';
    }
  } else if (onStarter) {
    text = (
      <span>
        <strong>You’re on a free Starter Plan.</strong>
      </span>
    );
    buttonText = 'View Plans';
    buttonDestination = '/pricing';
  }

  const showUpgradeButton = isWeb && Boolean(buttonText);
  const showBookDemoButton = isWeb && isNonPayingState;

  const onClickBuyNow = () => {
    if (buttonDestination) {
      navigate(buttonDestination);
    }
  };

  if (!trialBannerVisible || (!trial && !onStarter)) {
    return null;
  }

  return (
    <Toolbar
      sx={{
        backgroundColor: PVMediumPurple,
        height: BANNER_HEIGHT,
        minHeight: BANNER_HEIGHT,
        justifyContent: 'center',
        '@media print': {
          display: 'none',
        },
      }}
    >
      <Typography
        color="primary.contrastText"
        sx={{
          flex: { xs: '2 1', sm: 'initial' },
          marginRight: '12px',
        }}
      >
        {text}
      </Typography>
      {showUpgradeButton && (
        <Button
          size="small"
          color="secondary"
          sx={{ marginRight: showBookDemoButton ? '8px' : 0 }}
          onClick={onClickBuyNow}
          variant="contained"
        >
          {buttonText}
        </Button>
      )}
      {showBookDemoButton && (
        <Button
          size="small"
          color="info"
          variant="contained"
          data-cy="app-bar-contact-sales-button"
          target="_blank"
          href={BOOK_A_DEMO_URL}
        >
          Book a Demo
        </Button>
      )}
    </Toolbar>
  );
};

export default FreeTrialAppBar;
